/**
 * Predictive search enhancements.
 *
 * Adds to the existing search UI (desktop drawer + mobile header bar):
 *  - Recently viewed products (read from localStorage, written by a small
 *    snippet on the product template — see theme.liquid changes).
 *  - A default "Popular right now" grid before the customer types anything
 *    (desktop drawer only for now).
 *  - Query-suggestion chips and collection results in the live results,
 *    which requires asking the Predictive Search API for more than just
 *    products (resources[type]=product,collection,query).
 *
 * theme.js already binds its own keyup listeners to both search inputs
 * (desktop: [data-search-input], mobile: [data-mobile-search-input]) that
 * only request products. Rather than hand-edit that minified bundle, this
 * file replaces each input node — on window "load", and again every time
 * the field gains focus (see wireSearchInput/claim() below for why a single
 * clone on load turned out not to be enough) — and takes over the fetch
 * entirely. When this is folded into the real theme source, the cleaner fix
 * is to update the original fetch calls in-place — see the PR notes — and
 * this clone/replace shim can be deleted.
 */
(function () {
  var STORAGE_KEY = 'clubroom_recently_viewed';
  // Minimum characters before a search actually fires. Was 3 (fired once
  // length > 2) — dropped to 2 so short-but-specific queries (e.g. the "ZT"
  // putter) return results in the dropdown instead of requiring a full
  // page-load search to find them.
  var MIN_QUERY_LENGTH = 2;
  var RESOURCE_PARAMS = 'resources[type]=product,collection,query&resources[limit_scope]=each';

  function getRecentlyViewed() {
    try {
      var items = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(items) ? items : [];
    } catch (e) {
      return [];
    }
  }

  // Layout below uses inline styles rather than Tailwind utility classes —
  // see the comment in snippets/predictive-search-product.liquid for why
  // (this theme's compiled CSS only includes classes actually present in
  // its own Liquid source at build time; new class names silently have no
  // CSS behind them). Colour/weight classes (text-dark-blue, text-text-gray,
  // font-bold, font-semibold, bg-gray) are copied from the theme's existing
  // product card markup and are already compiled.
  function productTileHtml(p) {
    var image = p.image
      ? '<img src="' + p.image + '" alt="" loading="lazy" style="position:absolute;top:0;left:0;width:100%;height:100%;object-fit:contain;padding:10px;box-sizing:border-box;mix-blend-mode:multiply;">'
      : '';
    var price = p.price
      ? '<span class="font-semibold text-text-gray" style="font-size:12px;">' + p.price + '</span>'
      : '';
    return (
      '<div style="width:33.3333%;padding-left:6px;padding-right:6px;margin-bottom:16px;box-sizing:border-box;">' +
      '<a href="' + p.url + '" style="display:flex;flex-direction:column;gap:6px;text-decoration:none;">' +
      '<span class="bg-gray" style="position:relative;display:block;width:100%;padding-top:100%;border-radius:4px;overflow:hidden;">' + image + '</span>' +
      '<span class="font-bold text-dark-blue" style="font-size:12px;line-height:1.25;">' + p.title + '</span>' +
      price +
      '</a>' +
      '</div>'
    );
  }

  // querySelectorAll rather than querySelector: this now renders into BOTH
  // the desktop drawer's "Recently viewed" block AND the mobile default
  // panel's (they share the same data-recently-viewed / data-recently-
  // viewed-grid markup via snippets/predictive-search-default-panel.liquid)
  // — a single querySelector would only ever update whichever one happens
  // to come first in the DOM, leaving the other permanently stale.
  function renderRecentlyViewed() {
    var items = getRecentlyViewed();
    document.querySelectorAll('[data-recently-viewed]').forEach(function (wrap) {
      var grid = wrap.querySelector('[data-recently-viewed-grid]');
      if (!grid) return;
      if (!items.length) {
        wrap.classList.add('hidden');
        wrap.classList.remove('flex');
        grid.innerHTML = '';
        return;
      }
      grid.innerHTML = items.slice(0, 6).map(productTileHtml).join('');
      wrap.classList.remove('hidden');
      wrap.classList.add('flex');
    });
  }

  function toggleDesktopPanels(hasQuery) {
    var defaultPanel = document.querySelector('[data-predictive-search-default]');
    var resultsPanel = document.querySelector('[data-predictive-search-results]');
    if (!defaultPanel || !resultsPanel) return;
    defaultPanel.classList.toggle('hidden', hasQuery);
    resultsPanel.classList.toggle('hidden', !hasQuery);
    resultsPanel.classList.toggle('flex', hasQuery);
  }

  // Same default/results split as toggleDesktopPanels, but for the mobile
  // search bar's panel (data-mobile-search-default /
  // data-mobile-search-fetch-target in sections/header.liquid).
  function toggleMobilePanels(hasQuery) {
    var wrapper = document.getElementById('mobile-search-results');
    if (!wrapper) return;
    var defaultPanel = wrapper.querySelector('[data-mobile-search-default]');
    var resultsPanel = wrapper.querySelector('[data-mobile-search-fetch-target]');
    if (!defaultPanel || !resultsPanel) return;
    defaultPanel.classList.toggle('hidden', hasQuery);
    resultsPanel.classList.toggle('hidden', !hasQuery);
  }

  // querySelectorAll for the same reason as renderRecentlyViewed() above —
  // there are now two "Clear" buttons (desktop + mobile) that both need to
  // work independently.
  function bindClearButton() {
    document.querySelectorAll('[data-clear-recently-viewed]').forEach(function (clearBtn) {
      clearBtn.addEventListener('click', function () {
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch (e) {}
        renderRecentlyViewed();
      });
    });
  }

  function bindDrawerOpen() {
    document.querySelectorAll('.icon-search').forEach(function (btn) {
      btn.addEventListener('click', renderRecentlyViewed);
    });
  }

  // Collapsed collections list ("+N MORE COLLECTIONS" toggle). The results
  // panel's content is replaced wholesale via innerHTML on every keystroke
  // (see runSearch() below), and neither inline <script> tags nor
  // onclick="..." attributes execute reliably when content is inserted
  // that way — so this is wired once, here, via event delegation on
  // document rather than binding directly to the button each time it's
  // rendered. That means it keeps working no matter how many times the
  // results panel gets swapped out underneath it.
  function bindCollectionsToggle() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-toggle-collections]');
      if (!btn) return;
      var extra = btn.previousElementSibling;
      if (!extra || !extra.hasAttribute('data-extra-collections')) return;
      var isHidden = extra.style.display === 'none';
      extra.style.display = isHidden ? '' : 'none';
      btn.setAttribute('aria-expanded', isHidden ? 'true' : 'false');
      btn.textContent = isHidden
        ? btn.getAttribute('data-less-label') || 'SHOW LESS'
        : btn.getAttribute('data-more-label') || btn.textContent;
    });
  }

  /**
   * Wires one search input (desktop or mobile) to fetch the full resource
   * set and inject the response into resultsTarget. onToggle (optional)
   * lets the caller show/hide surrounding panels per-surface.
   */
  function wireSearchInput(config) {
    var form = document.querySelector(config.formSelector);
    var resultsTarget = document.querySelector(config.resultsSelector);
    if (!form || !resultsTarget) return;
    var wrapper = config.wrapperSelector ? document.querySelector(config.wrapperSelector) : null;

    var currentRequestId = 0;
    var debounceTimer = null;
    var input = null;
    var skipNextFocusReclaim = false;

    // config.revealOnType (mobile only — the desktop drawer already has its
    // own open/close mechanism via the .icon-search / data-search-close
    // buttons, unrelated to typing). Originally this revealed the results
    // wrapper as soon as the field was focused, before anyone had typed
    // anything. Changed per dev feedback: mobile has no dedicated close
    // button, so a drawer popping open on a bare tap — with nothing to
    // search yet — was a dead end if you didn't actually want to search.
    // Now the wrapper only opens once there's text in the box (see
    // onInput() below) and closes again the instant it's empty, whether
    // that's from backspacing all the way or tapping the native "×" clear
    // button a type="search" input shows once it has a value (that button
    // fires a real "input" event with an empty value, same as typing would,
    // so it's handled by the exact same code path). It also still closes on
    // a genuine click outside both the input and the wrapper.
    //
    // This deliberately uses inline style.display rather than toggling the
    // "hidden" class: this theme's own (unmodified) theme.js also has a
    // leftover document-level click-outside listener for this exact
    // wrapper, bound with a reference to whatever the mobile input was the
    // first time theme.js's own search code ran — since we now reclaim
    // (clone and replace) the input on every focus, that reference goes
    // stale, and theme.js's listener ends up treating clicks on our
    // current, correct input as "outside" and adding the "hidden" class
    // right back. Managing visibility via inline style means our own
    // show/hide always wins regardless of what that stray listener does to
    // the class list — inline style outranks a plain class selector.
    function showWrapper() {
      if (wrapper) wrapper.style.display = 'block';
    }
    function hideWrapper() {
      if (wrapper) wrapper.style.display = 'none';
    }

    // Confirmed root cause of the "results are sometimes missing chips/
    // collections, or flip back to a plain product list after already
    // rendering correctly" reports: this theme's own (unmodified) theme.js
    // wires up its own listener on this same input, doing its own fetch
    // with the old, narrower query (products only, capped at 10) and
    // writing into this same results panel. It has no staleness protection
    // of its own, so whichever response — ours or theirs — happens to
    // resolve last wins, regardless of which was more recent or more
    // relevant. Cloning the input once on page load (stripping whatever
    // was attached to the original node) was the original plan for
    // neutralizing this, but testing on the live site showed theme.js's
    // own wiring can attach itself to OUR clone if it happens to run after
    // us — the two scripts don't coordinate, so there's no reliable "we
    // always go last" ordering to depend on.
    //
    // The fix: reclaim (re-clone and replace) the input every time it gains
    // focus, not just once on load. Whatever may have attached to the node
    // in between — on load, in a delayed init, on some other lifecycle
    // hook — gets stripped right before the moment it actually matters:
    // the instant someone is about to start typing. This was verified live
    // (re-cloning the input on the actual site immediately stopped the old
    // handler's fetch from firing, and it did not come back on its own).
    function claim(refocus) {
      var current = document.querySelector(config.inputSelector) || input;
      if (!current) return;
      var fresh = current.cloneNode(true);
      current.parentNode.replaceChild(fresh, current);
      fresh.addEventListener('input', onInput);
      fresh.addEventListener('focus', onFocus);
      input = fresh;
      if (refocus) {
        // Cloning a focused element doesn't carry focus over, so restore
        // it immediately — otherwise the field (and the on-screen keyboard
        // on mobile) would drop out from under whoever just tapped in.
        // Re-focusing fires a genuine "focus" event on the new node, which
        // would otherwise trigger another reclaim immediately; the flag
        // below skips that one specific self-triggered call.
        skipNextFocusReclaim = true;
        fresh.focus();
        var len = fresh.value.length;
        try {
          fresh.setSelectionRange(len, len);
        } catch (e) {}
      }
    }

    function onFocus(e) {
      if (config.revealOnType) {
        renderRecentlyViewed();
        // A bare focus on an empty box no longer opens the drawer (see the
        // comment above) — but refocusing a box that already has text (e.g.
        // tapped away and back without clearing it) should still reopen it,
        // since typing has already "started" as far as this box is
        // concerned. e.target is the input actually receiving focus.
        var val = (e && e.target && e.target.value) || '';
        if (val.length > 0) showWrapper();
      }
      if (skipNextFocusReclaim) {
        skipNextFocusReclaim = false;
        return;
      }
      claim(true);
    }

    function runSearch(value) {
      var requestId = ++currentRequestId;
      fetch('/search/suggest?q=' + encodeURIComponent(value) + '&section_id=predictive-search&' + RESOURCE_PARAMS)
        .then(function (res) {
          if (!res.ok) throw new Error(String(res.status));
          return res.text();
        })
        .then(function (html) {
          // Two independent staleness checks, both required:
          //  1. requestId !== currentRequestId — a newer search has already
          //     started since this one was kicked off (normal fast-typing
          //     case).
          //  2. input.value !== value — the box no longer shows the text
          //     this response is even for. On mobile this can diverge from
          //     (1): virtual keyboards (autocorrect previews, predictive
          //     text, swipe-typing) can fire transient "input" events with
          //     an in-between value that doesn't match what's on screen a
          //     moment later, so a request can start, resolve, and still be
          //     the "latest" one by requestId, while the visible input has
          //     already reverted/changed to something else. Rendering that
          //     response would show results for text the customer isn't
          //     actually looking at, which matches the "results look wrong
          //     for what I typed" reports on mobile.
          if (requestId !== currentRequestId || input.value !== value) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var section = doc.querySelector('#shopify-section-predictive-search');
          // Only swap away from the default panel once there's actual
          // content ready to replace it with — see onInput() for why this
          // moved out of the keystroke handler (it used to hide the default
          // panel immediately on every keystroke, before the fetch had
          // resolved, which made the whole search screen flash empty for
          // however long the debounce + network round-trip took).
          if (config.onToggle) config.onToggle(true);
          resultsTarget.innerHTML = section ? section.innerHTML : '';
          if (config.onResults) config.onResults(resultsTarget);
          var button = resultsTarget.querySelector('[data-search-button]');
          if (button) {
            button.addEventListener('click', function (e) {
              e.preventDefault();
              form.submit();
            });
          }
        })
        .catch(function () {
          /* leave existing results in place on network error */
        });
    }

    // "input" rather than "keyup": mobile virtual keyboards don't reliably
    // fire keyup for every text change (autocomplete-bar taps, swipe-typing,
    // predictive text insertions, voice dictation all commonly skip it),
    // which is the most likely cause of search feeling inconsistent on
    // phones specifically. "input" fires for any value change regardless of
    // how the text got there, on every browser/device. The debounce below
    // avoids firing a fetch on every single keystroke while someone's still
    // typing — a request still starts within 200ms of the last change, and
    // the checks in runSearch() discard the response if it's stale by the
    // time it lands.
    function onInput(e) {
      var value = e.target.value;
      if (debounceTimer) clearTimeout(debounceTimer);

      // Wrapper visibility follows "is there any text at all", independent
      // of the MIN_QUERY_LENGTH search threshold below — see the
      // config.revealOnType comment above. showWrapper()/hideWrapper() are
      // no-ops when there's no wrapper (desktop), so this is safe to run
      // unconditionally for both surfaces.
      if (value.length > 0) {
        showWrapper();
      } else {
        hideWrapper();
      }

      if (value.length < MIN_QUERY_LENGTH) {
        // Below the search threshold — including back down to zero via
        // backspace. Previously this toggled to the (empty) results panel
        // immediately and only cleared its contents, which is exactly what
        // caused the "screen goes blank for a second" report: the default
        // panel (Recently viewed / Popular right now) disappeared the
        // instant you typed the first character, well before there was
        // anything to replace it with. Now it just reverts straight back to
        // the default panel instead, same as a fully-cleared box. Bumping
        // currentRequestId invalidates any request already in flight so a
        // stale response can't land afterwards and flip it back.
        currentRequestId++;
        if (config.onToggle) config.onToggle(false);
        resultsTarget.innerHTML = '';
        return;
      }
      debounceTimer = setTimeout(function () {
        runSearch(value);
      }, 200);
    }

    claim(false);
    if (!input) return;

    // Restore state if the input already has a value on load (e.g. back
    // button after a search). The default-panel -> results-panel toggle
    // itself now happens inside runSearch() once results are actually
    // ready, so there's nothing to pre-toggle here. Same "wrapper follows
    // whether there's text" rule as onInput() — open it immediately if this
    // input somehow starts pre-filled, rather than waiting for a keystroke.
    if (input.value.length > 0) showWrapper();
    if (input.value.length >= MIN_QUERY_LENGTH) {
      runSearch(input.value);
    }

    // Click-outside-to-close, mobile only (config.revealOnType). The desktop
    // drawer has its own dedicated close button/backdrop and isn't part of
    // this. Checked against the *current* input (not the captured variable
    // in this closure at bind time) since claim() replaces the node on every
    // focus — using the closure's `input` reference directly is fine here
    // because it's read at click time, after any reclaim has already run.
    if (config.revealOnType) {
      document.addEventListener('click', function (e) {
        if (!input) return;
        if (input.contains(e.target) || (wrapper && wrapper.contains(e.target))) return;
        hideWrapper();
      });
    }
  }

  window.addEventListener('load', function () {
    wireSearchInput({
      inputSelector: '[data-search-input]',
      formSelector: '[data-search-form]',
      resultsSelector: '[data-predictive-search-results]',
      onToggle: toggleDesktopPanels,
    });

    wireSearchInput({
      inputSelector: '[data-mobile-search-input]',
      formSelector: '#mobile-search-form',
      resultsSelector: '[data-mobile-search-fetch-target]',
      wrapperSelector: '#mobile-search-results',
      revealOnType: true,
      onToggle: toggleMobilePanels,
    });

    bindClearButton();
    bindDrawerOpen();
    bindCollectionsToggle();
    renderRecentlyViewed();
  });
})();
